import { Suspense, useMemo } from "react";
import * as THREE from "three";
import { useFrame, useThree } from "@react-three/fiber";
import {
  OrbitControls,
  useGLTF,
  Center,
  useTexture,
  Environment,
  Preload,
} from "@react-three/drei";
import {
  applyTextureByMaterialName,
  applyColorByMaterialName,
  tuneGlassOpacity,
  disableEnvMapExceptGlass,
} from "./materials.js";

// useGLTF caches scenes by path, so baselines must live outside the component
const doorBaselines = new Map();

const COLOR_DARK_BRONZE = { hex: "#362017", roughness: 0.5, metalness: 0.3 };
const DEFAULT_WOOD = "/textures/Alpine White W1100 ST9.jpg";

function WardrobeModel({ path, textures, doorsOpen }) {
  const { scene } = useGLTF(path);
  const loaded = useTexture({ wood: textures.baseUnit || DEFAULT_WOOD });
  const envTexture = useThree((state) => state.scene.environment);

  useMemo(() => {
    tuneGlassOpacity(scene, 0.2);
    applyColorByMaterialName(scene, "door_frame", COLOR_DARK_BRONZE);
    applyColorByMaterialName(scene, "door_frame2", COLOR_DARK_BRONZE);
    disableEnvMapExceptGlass(scene, envTexture);
  }, [scene, envTexture]);

  useMemo(() => {
    applyTextureByMaterialName(scene, "WOOD", loaded.wood, {
      lambert: true,
      envMap: envTexture,
    });
  }, [scene, loaded, envTexture]);

  const doorInfos = useMemo(() => {
    const seen = new Set();
    const infos = [];

    scene.traverse((child) => {
      if (!child.name.toLowerCase().startsWith("door")) return;

      let root = child;
      let cursor = child.parent;
      while (cursor) {
        if (cursor.name && cursor.name.toLowerCase().startsWith("door")) root = cursor;
        cursor = cursor.parent;
      }
      if (seen.has(root.uuid)) return;
      seen.add(root.uuid);

      if (!doorBaselines.has(root.uuid)) {
        const name = root.name.toLowerCase();
        doorBaselines.set(root.uuid, {
          initialRotationY: root.rotation.y,
          rotationSign: name.includes("_neg") ? -1 : 1,
          rotationDegrees: name.includes("_corner") ? 45 : 80,
        });
      }

      infos.push({ obj: root, ...doorBaselines.get(root.uuid) });
    });

    return infos;
  }, [scene]);

  useFrame(() => {
    doorInfos.forEach(({ obj, initialRotationY, rotationSign, rotationDegrees }) => {
      const target = doorsOpen
        ? initialRotationY + rotationSign * THREE.MathUtils.degToRad(rotationDegrees)
        : initialRotationY;
      obj.rotation.y = THREE.MathUtils.lerp(obj.rotation.y, target, 0.08);
    });
  });

  return (
    <Center>
      <primitive object={scene} />
    </Center>
  );
}

export default function Experience({ modelPath, textures, doorsOpen }) {
  return (
    <>
      <color attach="background" args={["#f7f7f7"]} />
      <OrbitControls makeDefault />
      <hemisphereLight args={["#ffffff", "#cccccc", 2.5]} />
      <directionalLight position={[0, 6, 5]} intensity={0.6} />
      <directionalLight position={[-5, 4, -3]} intensity={0.3} />
      <directionalLight position={[5, 4, -3]} intensity={0.3} />
      <Environment files="/lebombo_1k.hdr" />
      {modelPath && (
        <Suspense fallback={null}>
          <WardrobeModel path={modelPath} textures={textures} doorsOpen={doorsOpen} />
          <Preload all key={modelPath} />
        </Suspense>
      )}
    </>
  );
}
